import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
  AppLocale,
  DEFAULT_LOCALE,
  getLocaleConfig,
  LocaleConfig,
} from "./localesConfig";
import { parseLocaleFromPathname } from "./paths";
import { getStoredLocale, isAppPath, setStoredLocale } from "./localePreference";
import "./index";

type LocaleContextValue = {
  locale: AppLocale;
  config: LocaleConfig;
  pathnameWithoutLocale: string;
  isAppRoute: boolean;
  setAppLocale: (locale: AppLocale) => void;
};

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  config: getLocaleConfig(DEFAULT_LOCALE),
  pathnameWithoutLocale: "/",
  isAppRoute: false,
  setAppLocale: () => undefined,
});

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const [storedLocale, setStoredLocaleState] = useState<AppLocale>(
    () => getStoredLocale() ?? DEFAULT_LOCALE
  );

  const parsed = useMemo(
    () => parseLocaleFromPathname(location.pathname),
    [location.pathname]
  );
  const isAppRoute = isAppPath(location.pathname);
  const locale = isAppRoute ? storedLocale : parsed.locale;

  useEffect(() => {
    if (!isAppRoute && parsed.locale !== storedLocale) {
      setStoredLocale(parsed.locale);
      setStoredLocaleState(parsed.locale);
    }
  }, [isAppRoute, parsed.locale, storedLocale]);

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = getLocaleConfig(locale).htmlLang;
    }
  }, [i18n, locale]);

  const setAppLocale = useCallback((next: AppLocale) => {
    setStoredLocale(next);
    setStoredLocaleState(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      config: getLocaleConfig(locale),
      pathnameWithoutLocale: parsed.pathnameWithoutLocale,
      isAppRoute,
      setAppLocale,
    }),
    [locale, parsed.pathnameWithoutLocale, isAppRoute, setAppLocale]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextValue => useContext(LocaleContext);
